import { AgeGroup } from '@/types/user';

/**
 * Maps a specific age number to the appropriate age group range
 */
export const mapAgeToAgeGroup = (age: number): AgeGroup => {
  if (age <= 13) {
    return '13-and-under';
  } else if (age >= 14 && age <= 17) {
    return '14-17';
  } else {
    return '18-plus';
  }
};

/**
 * Maps individual age string values (1-13) to '13-and-under'
 */
export const normalizeAgeGroup = (ageGroup: AgeGroup): AgeGroup => {
  // If it's a single digit age (1-13), map it to '13-and-under'
  const numericAge = parseInt(ageGroup);
  if (!isNaN(numericAge) && numericAge >= 1 && numericAge <= 13) {
    return '13-and-under';
  }
  
  // Handle various formats and normalize to standard
  const ageStr = (ageGroup as string).toLowerCase();
  if (ageStr.includes('under') && ageStr.includes('13')) {
    return '13-and-under';
  }
  if (ageStr.includes('14') && ageStr.includes('17')) {
    return '14-17';
  }
  if (ageStr.includes('18') || ageStr.includes('plus') || ageStr.includes('adult')) {
    return '18-plus';
  }
  
  // Return the age group as-is if it's already a standard range
  return ageGroup;
};

/**
 * Checks if a player's age group is compatible with a game/team's required age group
 */
export const isAgeCompatible = (playerAgeGroup: AgeGroup, requiredAgeGroup: AgeGroup): boolean => {
  const normalizedPlayer = normalizeAgeGroup(playerAgeGroup);
  const normalizedRequired = normalizeAgeGroup(requiredAgeGroup);
  
  // Strict age compatibility for safety
  switch (normalizedRequired) {
    case '13-and-under':
      return normalizedPlayer === '13-and-under';
    case '14-17':
      return normalizedPlayer === '14-17' || normalizedPlayer === '13-and-under';
    case '18-plus':
      return normalizedPlayer === '18-plus';
    default:
      return false; // Default to incompatible for safety
  }
};

/**
 * Calculates age from birth date
 */
export const calculateAge = (birthDate: Date): number => {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};